import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Pencil, Trash2, ArrowLeft, ListChecks, Lock,
  Calendar as CalendarIcon, Tag, Flag, AlertTriangle, ChevronDown, ChevronUp, Receipt,
  CalendarPlus, Inbox,
} from "lucide-react";
import PanelLayout from "@/components/layout/PanelLayout";
import { COUNTRIES, countryLabel } from "@/constants/countries";
import DemoWriteBadge from "@/components/demo/DemoWriteBadge";

/**
 * A20-P5 — Wishlist Engine (Preview-only, per-user, EUR locked).
 *
 * Path: /app/wishlists
 *
 * Two-pane UX:
 *   - List view (default): all of user's wishlists.
 *   - Detail view: a selected wishlist + its items (CRUD).
 *
 * Marketplace toggles are intentionally not surfaced in P5.
 */

const WL_STATUSES = ["draft", "active", "paused", "completed", "archived"];
const ITEM_STATUSES = ["desired", "planned", "quoted", "purchased", "cancelled"];
const ITEM_CATEGORIES = [
  "equipment", "travel", "hotel", "transport", "food",
  "event_access", "training", "coaching", "medical", "physiotherapy",
  "technology", "marketing", "staff", "facilities", "insurance",
  "documentation", "gifts", "contingency", "other",
];
const PRIORITIES = ["high", "medium", "low"];

const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `€${Math.round(Number(n) || 0)}`; }
};

// Local-time today as ISO yyyy-mm-dd (NOT UTC — UTC would shift the date
// for users west of Greenwich late at night).
const todayLocalISO = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const emptyWishlistForm = () => ({
  title: "", description: "", country: "", city: "",
  // POST-DEPLOY-FIX-04 (final) — wishlist start date defaults to TODAY
  // (browser local). The user can still pick a future date; past dates
  // are blocked by `min` + submit guard. Format yyyy-mm-dd for
  // <input type="date">.
  start_date: todayLocalISO(),
  end_date: "", deadline_date: "",
  total_budget: "", status: "draft",
});

const emptyItemForm = () => ({
  category: "equipment", title: "", description: "",
  estimated_price: "", max_budget: "", quantity: 1,
  priority: "medium", required_by_date: "",
  status: "desired",
  // A20-P7
  related_event_id: "",
  required_date_user_touched: false,
});

const priorityClasses = {
  high:   { row: "border-rose-300 bg-rose-50/60",   pill: "bg-rose-600 text-white" },
  medium: { row: "border-slate-200 bg-white",       pill: "bg-amber-500 text-white" },
  low:    { row: "border-slate-200 bg-slate-50/60", pill: "bg-slate-300 text-slate-800" },
};

const LINE_STATUSES = ["planned", "quoted", "approved", "purchased", "cancelled"];

const emptyLineForm = () => ({
  category: "equipment", description: "",
  estimated_amount: "", max_budget: "", actual_amount: "",
  vendor_optional: "", due_date: "", required_by_date: "",
  priority: "medium", status: "planned",
});

// A20-P6 — inline sub-component for budget lines of a single item.
const BudgetLinesPanel = ({ wishlistId, itemId, t, onChange }) => {
  const [lines, setLines] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyLineForm());

  const fetchLines = useCallback(async () => {
    try {
      const r = await api.get(`/wishlists/${wishlistId}/items/${itemId}/budget-lines`);
      setLines(r.data?.lines || []);
    } catch {
      setLines([]);
    }
  }, [wishlistId, itemId]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyLineForm());
    setFormOpen(true);
  };
  const openEdit = (ln) => {
    setEditingId(ln.id);
    setForm({
      category: ln.category,
      description: ln.description || "",
      estimated_amount: ln.estimated_amount ?? "",
      max_budget: ln.max_budget ?? "",
      actual_amount: ln.actual_amount ?? "",
      vendor_optional: ln.vendor_optional || "",
      due_date: ln.due_date || "",
      required_by_date: ln.required_by_date || "",
      priority: ln.priority,
      status: ln.status,
    });
    setFormOpen(true);
  };
  const submit = async () => {
    const payload = {
      category: form.category,
      description: form.description.trim() || null,
      estimated_amount: Number(form.estimated_amount) || 0,
      max_budget: Number(form.max_budget) || 0,
      actual_amount: Number(form.actual_amount) || 0,
      vendor_optional: form.vendor_optional.trim() || null,
      due_date: form.due_date || null,
      required_by_date: form.required_by_date || null,
      priority: form.priority,
      status: form.status,
    };
    try {
      if (editingId) {
        await api.put(`/wishlists/${wishlistId}/items/${itemId}/budget-lines/${editingId}`, payload);
        toast.success(t("wishlist.lines.lineUpdated"));
      } else {
        await api.post(`/wishlists/${wishlistId}/items/${itemId}/budget-lines`, payload);
        toast.success(t("wishlist.lines.lineCreated"));
      }
      setFormOpen(false);
      await fetchLines();
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };
  const remove = async (ln) => {
    if (!window.confirm(t("wishlist.lines.confirmDelete"))) return;
    try {
      await api.delete(`/wishlists/${wishlistId}/items/${itemId}/budget-lines/${ln.id}`);
      toast.success(t("wishlist.lines.lineDeleted"));
      await fetchLines();
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`wl-lines-panel-${itemId}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-700 inline-flex items-center gap-1">
          <Receipt className="w-3.5 h-3.5" />
          {t("wishlist.lines.sectionTitle")} ({lines.length})
        </span>
        {!formOpen && (
          <Button size="sm" variant="outline" onClick={openCreate} data-testid={`wl-lines-add-${itemId}`}>
            <Plus className="w-3.5 h-3.5 me-1" />
            {t("wishlist.lines.addBtn")}
          </Button>
        )}
      </div>

      {formOpen && (
        <div className="bg-slate-50 border rounded p-3 space-y-2 text-xs" data-testid={`wl-lines-form-${itemId}`}>
          <div className="grid sm:grid-cols-2 gap-2">
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.item.category")}</span>
              <select className="w-full border rounded px-2 py-1.5"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid={`wl-lines-form-category-${itemId}`}>
                {ITEM_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{t(`wishlist.categories.${c}`) || c}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.form.status")}</span>
              <select className="w-full border rounded px-2 py-1.5"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid={`wl-lines-form-status-${itemId}`}>
                {LINE_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`wishlist.lineStatus.${s}`)}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-slate-600 mb-1">{t("wishlist.form.description")}</span>
            <Input value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={2000} data-testid={`wl-lines-form-description-${itemId}`} />
          </label>
          <div className="grid sm:grid-cols-3 gap-2">
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.estimated")}</span>
              <Input type="number" min="0" step="0.01" value={form.estimated_amount}
                onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })}
                data-testid={`wl-lines-form-est-${itemId}`} />
            </label>
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.maxBudget")}</span>
              <Input type="number" min="0" step="0.01" value={form.max_budget}
                onChange={(e) => setForm({ ...form, max_budget: e.target.value })}
                data-testid={`wl-lines-form-max-${itemId}`} />
            </label>
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.actual")}</span>
              <Input type="number" min="0" step="0.01" value={form.actual_amount}
                onChange={(e) => setForm({ ...form, actual_amount: e.target.value })}
                data-testid={`wl-lines-form-actual-${itemId}`} />
            </label>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.vendor")}</span>
              <Input value={form.vendor_optional}
                onChange={(e) => setForm({ ...form, vendor_optional: e.target.value })}
                maxLength={100} data-testid={`wl-lines-form-vendor-${itemId}`} />
            </label>
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.dueDate")}</span>
              <Input type="date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid={`wl-lines-form-due-${itemId}`} />
            </label>
            <label>
              <span className="block text-slate-600 mb-1">{t("wishlist.lines.requiredBy")}</span>
              <Input type="date" value={form.required_by_date}
                onChange={(e) => setForm({ ...form, required_by_date: e.target.value })}
                data-testid={`wl-lines-form-required-${itemId}`} />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setFormOpen(false)}
              data-testid={`wl-lines-form-cancel-${itemId}`}>
              {t("wishlist.cancel")}
            </Button>
            <Button size="sm" onClick={submit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`wl-lines-form-save-${itemId}`}>
              {t("wishlist.save")}
            </Button>
          </div>
        </div>
      )}

      {lines.length === 0 && !formOpen ? (
        <p className="text-xs text-slate-500 py-1" data-testid={`wl-lines-empty-${itemId}`}>
          {t("wishlist.lines.empty")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {lines.map((ln) => (
            <li key={ln.id}
              className="bg-white border rounded p-2 text-xs flex items-start gap-2"
              data-testid={`wl-line-${ln.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-800 border-0 text-[10px]">
                    {t(`wishlist.categories.${ln.category}`) || ln.category}
                  </Badge>
                  <Badge className="bg-emerald-100 text-emerald-900 border-0 text-[10px]">
                    {t(`wishlist.lineStatus.${ln.status}`)}
                  </Badge>
                  {ln.vendor_optional && (
                    <span className="text-slate-500">· {ln.vendor_optional}</span>
                  )}
                  {ln.due_date && (
                    <span className="text-slate-400">· {ln.due_date}</span>
                  )}
                </div>
                {ln.description && (
                  <div className="text-slate-700 mt-0.5 truncate">{ln.description}</div>
                )}
                <div className="text-slate-600 mt-0.5 tabular-nums flex flex-wrap gap-3">
                  <span>{t("wishlist.lines.estimated")}: <strong>{fmtEur(ln.estimated_amount)}</strong></span>
                  <span>{t("wishlist.lines.maxBudget")}: <strong>{fmtEur(ln.max_budget)}</strong></span>
                  <span>{t("wishlist.lines.actual")}: <strong>{fmtEur(ln.actual_amount)}</strong></span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openEdit(ln)}
                  data-testid={`wl-line-edit-${ln.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(ln)}
                  data-testid={`wl-line-delete-${ln.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MyWishlists = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const selected = rows.find((w) => w.id === selectedId) || null;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyWishlistForm());

  const [items, setItems] = useState([]);
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm());
  // A20-P7 — list of caller's linkable events
  const [linkableEvents, setLinkableEvents] = useState([]);
  // A20-P6 — track which item rows have their budget-lines panel expanded.
  const [expandedItems, setExpandedItems] = useState(() => new Set());
  const toggleExpand = (id) => setExpandedItems((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // ---------- Data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/wishlists");
      setRows(r.data?.wishlists || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async (wid) => {
    if (!wid) return;
    try {
      const r = await api.get(`/wishlists/${wid}/items`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (selectedId) fetchItems(selectedId); }, [selectedId, fetchItems]);

  // A20-P7 — load linkable events lazily when the item drawer opens.
  useEffect(() => {
    if (!itemFormOpen) return;
    api.get("/wishlists/calendar-linkable-events")
      .then((r) => setLinkableEvents(r.data?.events || []))
      .catch(() => setLinkableEvents([]));
  }, [itemFormOpen]);

  // ---------- Wishlist CRUD ----------
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyWishlistForm());
    setDrawerOpen(true);
  };
  const openEdit = (wl) => {
    setEditingId(wl.id);
    setForm({
      title: wl.title || "",
      description: wl.description || "",
      country: wl.country || "",
      city: wl.city || "",
      start_date: wl.start_date || "",
      end_date: wl.end_date || "",
      deadline_date: wl.deadline_date || "",
      total_budget: wl.total_budget ?? "",
      status: wl.status || "draft",
    });
    setDrawerOpen(true);
  };
  const submitForm = async () => {
    // POST-DEPLOY-FIX-03 — wishlist date sanity. start/end/deadline must be
    // chronologically consistent. Server still validates but we surface a
    // localised error immediately so the admin doesn't see the network 400.
    //
    // POST-DEPLOY-FIX-04 (final) — on CREATE, the start date cannot be in
    // the past. On EDIT we allow a stale past start_date to remain so a
    // user editing an old wishlist isn't blocked from updating title /
    // budget / etc. — only NEW values must be valid going forward.
    const today = todayLocalISO();
    if (!editingId && form.start_date && form.start_date < today) {
      toast.error(t("wishlist.toasts.startInPast"));
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error(t("wishlist.toasts.endBeforeStart"));
      return;
    }
    if (form.deadline_date && form.end_date && form.deadline_date < form.end_date) {
      toast.error(t("wishlist.toasts.deadlineBeforeEnd"));
      return;
    }
    if (form.deadline_date && form.start_date && form.deadline_date < form.start_date) {
      toast.error(t("wishlist.toasts.deadlineBeforeEnd"));
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      country: form.country.trim() || null,
      city: form.city.trim() || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      deadline_date: form.deadline_date || null,
      total_budget: Number(form.total_budget) || 0,
      status: form.status,
    };
    try {
      if (editingId) {
        await api.put(`/wishlists/${editingId}`, payload);
        toast.success(t("wishlist.toasts.updated"));
      } else {
        await api.post("/wishlists", payload);
        toast.success(t("wishlist.toasts.created"));
      }
      setDrawerOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };
  const archiveWishlist = async (wl) => {
    if (!window.confirm(t("wishlist.confirmDelete"))) return;
    try {
      await api.delete(`/wishlists/${wl.id}`);
      toast.success(t("wishlist.toasts.deleted"));
      if (selectedId === wl.id) setSelectedId(null);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };

  // ---------- Item CRUD ----------
  const openCreateItem = () => {
    setEditingItemId(null);
    setItemForm(emptyItemForm());
    setItemFormOpen(true);
  };
  const openEditItem = (it) => {
    setEditingItemId(it.id);
    setItemForm({
      category: it.category,
      title: it.title || "",
      description: it.description || "",
      estimated_price: it.estimated_price ?? "",
      max_budget: it.max_budget ?? "",
      quantity: it.quantity || 1,
      priority: it.priority,
      required_by_date: it.required_by_date || "",
      status: it.status,
      related_event_id: it.related_event_id || "",
      required_date_user_touched: !!it.required_by_date,
    });
    setItemFormOpen(true);
  };
  const submitItem = async () => {
    const payload = {
      category: itemForm.category,
      title: itemForm.title.trim(),
      description: itemForm.description.trim() || null,
      estimated_price: Number(itemForm.estimated_price) || 0,
      max_budget: Number(itemForm.max_budget) || 0,
      quantity: Math.max(1, Number(itemForm.quantity) || 1),
      priority: itemForm.priority,
      required_by_date: itemForm.required_by_date || null,
      status: itemForm.status,
      // A20-P7 — null clears the link; empty string means "unlink".
      related_event_id: itemForm.related_event_id || null,
    };
    try {
      if (editingItemId) {
        await api.put(`/wishlists/${selectedId}/items/${editingItemId}`, payload);
        toast.success(t("wishlist.toasts.itemUpdated"));
      } else {
        await api.post(`/wishlists/${selectedId}/items`, payload);
        toast.success(t("wishlist.toasts.itemCreated"));
      }
      setItemFormOpen(false);
      await fetchItems(selectedId);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };
  const deleteItem = async (it) => {
    if (!window.confirm(t("wishlist.item.confirmDelete"))) return;
    try {
      await api.delete(`/wishlists/${selectedId}/items/${it.id}`);
      toast.success(t("wishlist.toasts.itemDeleted"));
      await fetchItems(selectedId);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
    }
  };

  // A20-P8.1 — auto-create a calendar event from a wishlist item and link them.
  const planInCalendar = async (it) => {
    if (!it.required_by_date) {
      toast.error(t("wishlist.link.planDisabledHint"));
      return;
    }
    try {
      await api.post(`/wishlists/${selectedId}/items/${it.id}/plan-in-calendar`);
      toast.success(t("wishlist.link.planCreatedToast"));
      await fetchItems(selectedId);
      await fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(t("wishlist.link.planAlreadyLinked"));
      } else if (typeof detail === "string" && detail.includes("required_by_date")) {
        toast.error(t("wishlist.link.planDisabledHint"));
      } else {
        toast.error(extractErrorMessage(err, t("wishlist.toasts.saveFailed")));
      }
    }
  };

  // A20-P9 — create a private marketplace request from a wishlist item.
  // Country defaults to ES (pilot scope); user can edit it later in the requests hub.
  const prepareRequest = async (it) => {
    try {
      const payload = {
        source_wishlist_id: selectedId,
        source_item_id: it.id,
        title: it.title,
        description: it.description || null,
        category: it.category,
        subcategory: it.subcategory || null,
        quantity: it.quantity || 1,
        max_budget: Number(it.max_budget) || 0,
        required_by_date: it.required_by_date || null,
        related_event_id: it.related_event_id || null,
        country: "ES",
      };
      await api.post("/marketplace-requests", payload);
      toast.success(t("marketplace.toasts.created"));
    } catch (err) {
      toast.error(extractErrorMessage(err, t("marketplace.toasts.saveFailed")));
    }
  };

  // ---------- Render ----------
  return (
    <PanelLayout>
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="my-wishlists-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX3 — the global "back to
                /app" entry is now in the persistent side menu (`Panel`).
                We only keep this inline back button to climb OUT of a
                selected wishlist back to the wishlist list. */}
            {selected && (
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="tsw-btn-back mb-2"
                data-testid="my-wishlists-back-btn"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
                {t("wishlist.back")}
              </button>
            )}
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="my-wishlists-title">
              <ListChecks className="w-6 h-6 text-emerald-700" />
              {selected ? selected.title : t("wishlist.pageTitle")}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t("wishlist.pageSub")}</p>
            <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t("wishlist.privacy")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={fetchAll} disabled={loading}
              data-testid="my-wishlists-refresh"
            >
              <RefreshCw className="w-4 h-4 me-1" />
            </Button>
            {!selected && (
              <Button
                size="sm"
                onClick={openCreate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="my-wishlists-create-btn"
              >
                <Plus className="w-4 h-4 me-1" />
                {t("wishlist.newBtn")}
              </Button>
            )}
            {selected && (
              <Button
                size="sm"
                onClick={openCreateItem}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="my-wishlists-item-create-btn"
              >
                <Plus className="w-4 h-4 me-1" />
                {t("wishlist.item.addBtn")}
              </Button>
            )}
          </div>
        </div>

        {/* List view */}
        {!selected && (
          <>
            {rows.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500 text-sm" data-testid="my-wishlists-empty">
                    {t("wishlist.empty")}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-wishlists-grid">
                {rows.map((wl) => {
                  const r = wl.rollups || {};
                  const pct = Number(r.progress_percentage || 0);
                  const overdue = Number(r.overdue_items_count || 0);
                  return (
                  <Card
                    key={wl.id}
                    className="hover:border-emerald-400 transition cursor-pointer"
                    onClick={() => setSelectedId(wl.id)}
                    data-testid={`my-wishlists-card-${wl.id}`}
                  >
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display font-semibold text-base text-slate-900 line-clamp-2">
                          {wl.title}
                        </h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <DemoWriteBadge state={wl._demo_state} />
                          <Badge className="bg-slate-100 text-slate-800 border-0 text-[10px] uppercase">
                            {t(`wishlist.status.${wl.status}`)}
                          </Badge>
                        </div>
                      </div>
                      {wl.description && (
                        <p className="text-xs text-slate-500 line-clamp-2">{wl.description}</p>
                      )}
                      {/* A20-P6 — Progress bar */}
                      <div className="space-y-1" data-testid={`wl-card-progress-${wl.id}`}>
                        <div className="flex items-center justify-between text-[11px] text-slate-600">
                          <span className="inline-flex items-center gap-1">
                            {t("wishlist.rollups.progress")} · {r.purchased_items_count || 0}/{r.items_count || 0} {t("wishlist.rollups.purchased")}
                          </span>
                          <span className="font-semibold tabular-nums">{pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 transition-all"
                            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 pt-1">
                        <span className="inline-flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {wl.items_count || 0} {t("wishlist.card.items")}
                        </span>
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          {t("wishlist.card.budget")}: <strong>{fmtEur(wl.total_budget)}</strong>
                        </span>
                        {r.next_deadline && (
                          <span className="inline-flex items-center gap-1" data-testid={`wl-card-next-deadline-${wl.id}`}>
                            <CalendarIcon className="w-3 h-3" />
                            {t("wishlist.rollups.nextDeadline")}: <strong>{r.next_deadline}</strong>
                          </span>
                        )}
                        {overdue > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-rose-700 font-semibold"
                            data-testid={`wl-card-overdue-${wl.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {overdue} {t("wishlist.rollups.overdue")}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-end gap-1 pt-1 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(wl)} data-testid={`my-wishlists-edit-${wl.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => archiveWishlist(wl)} data-testid={`my-wishlists-delete-${wl.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Detail view (items) */}
        {selected && (
          <div className="space-y-3" data-testid="my-wishlists-detail">
            <Card>
              <CardContent className="p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
                <div>
                  <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.form.status")}</div>
                  <div className="font-semibold">{t(`wishlist.status.${selected.status}`)}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.rollups.progress")}</div>
                  <div className="font-semibold tabular-nums">
                    {Number(selected.rollups?.progress_percentage || 0).toFixed(0)}%
                    <span className="text-[10px] text-slate-500 ms-1">
                      ({selected.rollups?.purchased_items_count || 0}/{selected.rollups?.items_count || 0})
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.rollups.estimatedTotal")}</div>
                  <div className="font-semibold tabular-nums" data-testid="wl-detail-estimated">
                    {fmtEur(selected.rollups?.estimated_total)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.rollups.actualTotal")}</div>
                  <div className="font-semibold tabular-nums" data-testid="wl-detail-actual">
                    {fmtEur(selected.rollups?.actual_total)}
                  </div>
                </div>
                {selected.rollups?.next_deadline ? (
                  <div>
                    <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.rollups.nextDeadline")}</div>
                    <div className="font-semibold" data-testid="wl-detail-next-deadline">
                      {selected.rollups.next_deadline}
                      {Number(selected.rollups?.overdue_items_count || 0) > 0 && (
                        <span className="ms-2 text-rose-700 text-[10px] inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {selected.rollups.overdue_items_count} {t("wishlist.rollups.overdue")}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-slate-500 text-[10px] uppercase">{t("wishlist.rollups.maxTotal")}</div>
                    <div className="font-semibold tabular-nums">{fmtEur(selected.rollups?.max_budget_total)}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {items.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-slate-500" data-testid="my-wishlists-items-empty">
                    {t("wishlist.item.empty")}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2" data-testid="my-wishlists-items-list">
                {items.map((it) => {
                  const pri = priorityClasses[it.priority] || priorityClasses.medium;
                  const isExpanded = expandedItems.has(it.id);
                  return (
                    <li
                      key={it.id}
                      className={`border rounded p-3 ${pri.row}`}
                      data-testid={`my-wishlists-item-${it.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className={`${pri.pill} border-0 text-[10px] uppercase`} data-testid={`my-wishlists-item-priority-${it.id}`}>
                              <Flag className="w-3 h-3 me-1" />
                              {t(`wishlist.priority.${it.priority}`)}
                            </Badge>
                            <Badge className="bg-slate-100 text-slate-800 border-0 text-[10px]">
                              {t(`wishlist.categories.${it.category}`) || it.category}
                            </Badge>
                            <Badge className="bg-emerald-100 text-emerald-900 border-0 text-[10px]">
                              {t(`wishlist.itemStatus.${it.status}`)}
                            </Badge>
                            <DemoWriteBadge state={it._demo_state} />
                          </div>
                          <div className="font-semibold text-slate-900 mt-1 truncate">{it.title}</div>
                          {it.description && (
                            <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{it.description}</div>
                          )}
                          <div className="text-xs text-slate-600 mt-1 tabular-nums flex flex-wrap gap-3">
                            <span>{t("wishlist.item.estimated")}: <strong>{fmtEur(it.estimated_price)}</strong></span>
                            <span>{t("wishlist.item.maxBudget")}: <strong>{fmtEur(it.max_budget)}</strong></span>
                            <span>{t("wishlist.item.quantity")}: <strong>{it.quantity}</strong></span>
                            {it.required_by_date && (
                              <span>{t("wishlist.item.requiredBy")}: <strong>{it.required_by_date}</strong></span>
                            )}
                          </div>
                          {/* A20-P7 — Linked-event chip */}
                          {it.related_event_id && (
                            <div
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                              data-testid={`wl-item-link-${it.id}`}
                            >
                              <CalendarIcon className="w-3 h-3" />
                              <span className="truncate max-w-[180px]">
                                {t("wishlist.link.label")}
                              </span>
                              <button
                                type="button"
                                className="underline hover:text-sky-900 ms-1"
                                onClick={(e) => { e.stopPropagation(); navigate("/app/calendar"); }}
                                data-testid={`wl-item-view-cal-${it.id}`}
                              >
                                {t("wishlist.link.viewInCal")}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {!it.related_event_id && (
                            <Button
                              size="sm" variant="outline"
                              className="text-violet-700 border-violet-200 hover:bg-violet-50 hidden sm:inline-flex"
                              onClick={(e) => { e.stopPropagation(); planInCalendar(it); }}
                              disabled={!it.required_by_date}
                              title={!it.required_by_date ? t("wishlist.link.planDisabledHint") : t("wishlist.link.planBtn")}
                              data-testid={`wl-item-plan-cal-${it.id}`}
                            >
                              <CalendarPlus className="w-3.5 h-3.5 me-1" />
                              <span className="hidden md:inline">{t("wishlist.link.planBtn")}</span>
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline"
                            className="text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50 hidden sm:inline-flex"
                            onClick={(e) => { e.stopPropagation(); prepareRequest(it); }}
                            title={t("marketplace.actions.createFromItem")}
                            data-testid={`wl-item-prepare-request-${it.id}`}
                          >
                            <Inbox className="w-3.5 h-3.5 me-1" />
                            <span className="hidden md:inline">{t("marketplace.actions.createFromItem")}</span>
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => toggleExpand(it.id)}
                            data-testid={`my-wishlists-item-toggle-${it.id}`}
                            title={isExpanded ? t("wishlist.lines.hide") : t("wishlist.lines.show")}
                          >
                            {isExpanded
                              ? <ChevronUp className="w-3.5 h-3.5" />
                              : <ChevronDown className="w-3.5 h-3.5" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditItem(it)} data-testid={`my-wishlists-item-edit-${it.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteItem(it)} data-testid={`my-wishlists-item-delete-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        </div>
                      </div>
                      {isExpanded && (
                        <BudgetLinesPanel
                          wishlistId={selectedId}
                          itemId={it.id}
                          t={t}
                          onChange={() => { fetchAll(); }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Wishlist create/edit drawer */}
        <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-wishlists-drawer">
            <DialogHeader>
              <DialogTitle>{editingId ? t("wishlist.edit") : t("wishlist.newBtn")}</DialogTitle>
              <DialogDescription className="sr-only">
                {editingId ? t("wishlist.edit") : t("wishlist.newBtn")}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-3 text-sm">
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.title")} *</span>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={200} data-testid="my-wishlists-form-title" />
              </label>
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.description")}</span>
                <Textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={2000} data-testid="my-wishlists-form-description" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.country")}</span>
                  {/* WL-COUNTRY-CITY-FIX — Country must always be a controlled
                      dropdown (ISO-2 codes from the shared `COUNTRIES`
                      constant, rendered with localised labels via
                      `countryLabel`). We tolerate legacy free-text values
                      written before this hotfix by surfacing them as an
                      extra option so editing an old wishlist doesn't
                      silently lose the country. */}
                  <select
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })}
                    data-testid="my-wishlists-form-country"
                  >
                    <option value="">—</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{countryLabel(c.code, t)}</option>
                    ))}
                    {form.country && !COUNTRIES.some((c) => c.code === form.country) && (
                      <option value={form.country}>{form.country}</option>
                    )}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.city")}</span>
                  {/* WL-COUNTRY-CITY-FIX — TEMPORARY FALLBACK: no per-country
                      cities catalog exists yet, so City stays as a free-text
                      input. It is disabled until a country is picked, which
                      makes the dependency explicit and avoids rendering an
                      empty dropdown shell. When the cities catalog ships,
                      swap this <Input> for a dependent <select> driven by
                      `form.country`. */}
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    maxLength={100}
                    disabled={!form.country}
                    data-testid="my-wishlists-form-city"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.startDate")}</span>
                  <Input type="date" value={form.start_date}
                    min={!editingId ? todayLocalISO() : undefined}
                    onChange={(e) => {
                      const next = e.target.value;
                      setForm((f) => {
                        const draft = { ...f, start_date: next };
                        // Cascade: if new start invalidates end/deadline,
                        // clear the downstream fields so the user picks
                        // afresh.
                        if (next && f.end_date && f.end_date < next) draft.end_date = "";
                        if (next && f.deadline_date && f.deadline_date < next) draft.deadline_date = "";
                        return draft;
                      });
                    }}
                    aria-invalid={!editingId && form.start_date && form.start_date < todayLocalISO()}
                    data-testid="my-wishlists-form-start" />
                  {!editingId && form.start_date && form.start_date < todayLocalISO() && (
                    <span className="block text-xs text-rose-700 mt-1" data-testid="my-wishlists-form-start-error">
                      {t("wishlist.toasts.startInPast")}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.endDate")}</span>
                  <Input type="date" value={form.end_date}
                    min={form.start_date || undefined}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    aria-invalid={form.start_date && form.end_date && form.end_date < form.start_date}
                    data-testid="my-wishlists-form-end" />
                  {form.start_date && form.end_date && form.end_date < form.start_date && (
                    <span className="block text-xs text-rose-700 mt-1" data-testid="my-wishlists-form-end-error">
                      {t("wishlist.toasts.endBeforeStart")}
                    </span>
                  )}
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.deadline")}</span>
                  <Input type="date" value={form.deadline_date}
                    min={form.end_date || form.start_date || undefined}
                    onChange={(e) => setForm({ ...form, deadline_date: e.target.value })}
                    aria-invalid={form.deadline_date && form.end_date && form.deadline_date < form.end_date}
                    data-testid="my-wishlists-form-deadline" />
                  {form.deadline_date && form.end_date && form.deadline_date < form.end_date && (
                    <span className="block text-xs text-rose-700 mt-1" data-testid="my-wishlists-form-deadline-error">
                      {t("wishlist.toasts.deadlineBeforeEnd")}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.totalBudget")}</span>
                  <Input type="number" min="0" step="0.01" value={form.total_budget}
                    onChange={(e) => setForm({ ...form, total_budget: e.target.value })}
                    data-testid="my-wishlists-form-budget" />
                </label>
              </div>
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.status")}</span>
                <select className="w-full border rounded px-2 py-1.5"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="my-wishlists-form-status">
                  {WL_STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`wishlist.status.${s}`)}</option>
                  ))}
                </select>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDrawerOpen(false)} data-testid="my-wishlists-form-cancel">
                  {t("wishlist.cancel")}
                </Button>
                <Button
                  onClick={submitForm}
                  disabled={!form.title.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="my-wishlists-form-save"
                >
                  {t("wishlist.save")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Item create/edit drawer */}
        <Sheet open={itemFormOpen} onOpenChange={setItemFormOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="my-wishlists-item-drawer">
            <SheetHeader>
              <SheetTitle>{editingItemId ? t("wishlist.edit") : t("wishlist.item.addBtn")}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3 text-sm">
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.title")} *</span>
                <Input value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  maxLength={200} data-testid="my-wishlists-item-form-title" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.category")}</span>
                  <select className="w-full border rounded px-2 py-1.5"
                    value={itemForm.category}
                    onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                    data-testid="my-wishlists-item-form-category">
                    {ITEM_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{t(`wishlist.categories.${c}`) || c}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.priority.label")}</span>
                  <select className="w-full border rounded px-2 py-1.5"
                    value={itemForm.priority}
                    onChange={(e) => setItemForm({ ...itemForm, priority: e.target.value })}
                    data-testid="my-wishlists-item-form-priority">
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{t(`wishlist.priority.${p}`)}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="block text-slate-600 mb-1">{t("wishlist.form.description")}</span>
                <Textarea rows={2} value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  maxLength={2000} data-testid="my-wishlists-item-form-description" />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.estimated")}</span>
                  <Input type="number" min="0" step="0.01" value={itemForm.estimated_price}
                    onChange={(e) => setItemForm({ ...itemForm, estimated_price: e.target.value })}
                    data-testid="my-wishlists-item-form-estimated" />
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.maxBudget")}</span>
                  <Input type="number" min="0" step="0.01" value={itemForm.max_budget}
                    onChange={(e) => setItemForm({ ...itemForm, max_budget: e.target.value })}
                    data-testid="my-wishlists-item-form-maxbudget" />
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.quantity")}</span>
                  <Input type="number" min="1" step="1" value={itemForm.quantity}
                    onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                    data-testid="my-wishlists-item-form-quantity" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.item.requiredBy")}</span>
                  <Input type="date" value={itemForm.required_by_date}
                    onChange={(e) => setItemForm({ ...itemForm, required_by_date: e.target.value, required_date_user_touched: true })}
                    data-testid="my-wishlists-item-form-required" />
                </label>
                <label className="block">
                  <span className="block text-slate-600 mb-1">{t("wishlist.form.status")}</span>
                  <select className="w-full border rounded px-2 py-1.5"
                    value={itemForm.status}
                    onChange={(e) => setItemForm({ ...itemForm, status: e.target.value })}
                    data-testid="my-wishlists-item-form-status">
                    {ITEM_STATUSES.map((s) => (
                      <option key={s} value={s}>{t(`wishlist.itemStatus.${s}`)}</option>
                    ))}
                  </select>
                </label>
              </div>
              {/* A20-P7 — Linked calendar event selector */}
              <label className="block">
                <span className="block text-slate-600 mb-1 inline-flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {t("wishlist.link.label")}
                </span>
                <select className="w-full border rounded px-2 py-1.5"
                  value={itemForm.related_event_id}
                  onChange={(e) => {
                    const newId = e.target.value;
                    const ev = linkableEvents.find((x) => x.id === newId);
                    setItemForm((f) => {
                      const next = { ...f, related_event_id: newId };
                      // Auto-fill required_by_date only if the user hasn't
                      // typed one yet AND a real event is selected.
                      if (newId && ev && !f.required_date_user_touched && !f.required_by_date) {
                        next.required_by_date = String(ev.starts_at || "").slice(0, 10);
                        toast.success(t("wishlist.link.autofilledToast"));
                      }
                      return next;
                    });
                  }}
                  data-testid="my-wishlists-item-form-event">
                  <option value="">{t("wishlist.link.none")}</option>
                  {linkableEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>{`${ev.title} · ${String(ev.starts_at || "").slice(0, 10)}`}</option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-500 mt-1 block">
                  {t("wishlist.link.autofillHint")}
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setItemFormOpen(false)} data-testid="my-wishlists-item-form-cancel">
                  {t("wishlist.cancel")}
                </Button>
                <Button
                  onClick={submitItem}
                  disabled={!itemForm.title.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="my-wishlists-item-form-save"
                >
                  {t("wishlist.save")}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
    </PanelLayout>
  );
};

export default MyWishlists;
